// src/hooks/plan/useMemberManagePage.js
import { useState, useMemo, useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import planApi from '@api/planApi'; 
import { normalizePage } from '@utils/paging';
import { AuthContext } from '@context';

export default function useMemberManagePage() {
  const queryClient = useQueryClient();
  const { auth } = useContext(AuthContext);
  
  const [filters, setFilters] = useState({
    branch: '',
    status: '전체',
    searchKeyword: '',
    isAdminChecked: false, // 관리자만 보기 체크박스 상태
    pageNum: 1,
    pageSize: 10
  });

  const [modal, setModal] = useState({ type: null, member: null });

  const [isExcelOpen, setIsExcelOpen] = useState(false);

  // API 요청용 파라미터 변환
  const queryParams = useMemo(() => ({
    ...filters,
    roleId: filters.isAdminChecked ? 'R0' : 'R2'
  }), [filters]);

  const query = useQuery({
    queryKey: ['plan', 'members', queryParams],
    queryFn: () => planApi.listMembers(queryParams), 
    keepPreviousData: true,
  });

  const page = useMemo(() => normalizePage(query.data) || { rows: [], total: 0, pageNum: filters.pageNum, pageSize: filters.pageSize }, [query.data]);

  // 공통 캐시 무효화 함수
  const invalidateMembers = () => queryClient.invalidateQueries({ queryKey: ['plan', 'members'] });

  // 회원 등록 Mutation
  const createMutation = useMutation({
    mutationFn: (payload) => planApi.createMember(payload),
    onSuccess: invalidateMembers
  });

  // 회원 수정 Mutation
  const updateMutation = useMutation({
    mutationFn: ({ memberId, payload }) => planApi.updateMember(memberId, payload),
    onSuccess: invalidateMembers
  });

  // 회원 삭제 Mutation
  const deleteMutation = useMutation({
    mutationFn: (memberId) => planApi.deleteMember(memberId), 
    onSuccess: invalidateMembers
  });

  // 🌟 (추가) 엑셀 업로드 Mutation
  const excelUploadMutation = useMutation({
    mutationFn: (file) => planApi.uploadMemberExcel(file), // API 호출 (백엔드 추가 구현 필요)
    onSuccess: () => {
      invalidateMembers();
      setIsExcelOpen(false); 
    }
  });

  // 🌟 (추가) 템플릿 다운로드 핸들러
  const handleDownloadTemplate = async () => {
    try {
      const response = await planApi.downloadMemberExcelTemplate();
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', '회원_등록양식.xlsx'); 
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (e) {
      throw e; 
    }
  };

  const onSearch = () => setFilters(p => ({ ...p, pageNum: 1 }));

  return {
    filters, setFilters, query, page, onSearch,
    modal, setModal,
    isExcelOpen, setIsExcelOpen,
    uploadExcel: excelUploadMutation.mutateAsync, isUploadingExcel: excelUploadMutation.isPending,
    downloadTemplate: handleDownloadTemplate,
    createMember: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateMember: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteMember: deleteMutation.mutateAsync,
    auth
  };
}